import { useState, useEffect, useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { DenominationCounter, DenominationTable, denominationTotal, emptyDenominations } from '@/components/store/DenominationCounter';
import { 
  DollarSign, Clock, CheckCircle2, AlertTriangle, History, 
  Play, Square, RefreshCw, Banknote, Monitor, User, Users, Eye, Printer, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/AuthContext';
import { useStoreTerminal } from '@/components/store/TerminalSelector';
import CashRegisterReport from '@/components/store/CashRegisterReport';
import { formatDate, formatTimeOnly } from '@/utils/dateFormat';
import { posAPI, storesAPI } from '@/services/api';

const CashRegisterPage = () => {
  const [currentRegister, setCurrentRegister] = useState(null);
  const [registerHistory, setRegisterHistory] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-register');
  
  // Terminal status for manager view
  const [terminalsStatus, setTerminalsStatus] = useState([]);
  const [terminalsSummary, setTerminalsSummary] = useState({});
  
  // Selected terminal for manager close
  const [selectedRegisterToClose, setSelectedRegisterToClose] = useState(null);
  
  // Open dialog
  const [openDialogOpen, setOpenDialogOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState('');
  const [openingBalance, setOpeningBalance] = useState('');
  const [openingNotes, setOpeningNotes] = useState('');
  
  // Close dialog
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [actualBalance, setActualBalance] = useState('');
  const [denominations, setDenominations] = useState(emptyDenominations());
  const [closingNotes, setClosingNotes] = useState('');
  
  // Report dialog
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReportRegister, setSelectedReportRegister] = useState(null);
  const reportRef = useRef(null);
  const initialLoadDone = useRef(false);
  
  const [submitting, setSubmitting] = useState(false);
  
  const { user } = useAuth();
  const { toast } = useToast();
  const terminalContext = useStoreTerminal();
  const storeId = user?.entity_id;
  const isManager = user?.role_name?.toLowerCase().includes('manager');
  
  // Get selected terminal ID for counter operations
  const selectedTerminalId = terminalContext?.selectedTerminal;
  const currentTerminal = terminalContext?.currentTerminal;

  // Print handler
  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `CashRegister_${selectedReportRegister?.counter_id}_${selectedReportRegister?.register_id?.slice(0, 8) || 'report'}`,
  });

  // Open report for a register
  const openReport = (register) => {
    setSelectedReportRegister(register);
    setReportDialogOpen(true);
  };

  const fetchData = useCallback(async () => {
    if (!storeId) return;
    
    setLoading((prev) => (initialLoadDone.current ? prev : true));
    try {
      // Get store details for shifts
      const storeRes = await storesAPI.get(storeId);
      setStore(storeRes.data);
      
      // Get current register for selected terminal
      const counterId = selectedTerminalId || storeRes.data?.counters?.[0]?.counter_id || 'C01';
      const regRes = await posAPI.getCurrentRegister(storeId, counterId);
      setCurrentRegister(regRes.data?.register);
      
      // Get register history
      const histRes = await posAPI.getRegisterHistory(storeId);
      setRegisterHistory(histRes.data || []);
      
      // Get all terminals status (for manager view)
      if (isManager) {
        const termStatusRes = await posAPI.getTerminalsRegisterStatus(storeId);
        setTerminalsStatus(termStatusRes.data.terminals || []);
        setTerminalsSummary(termStatusRes.data.summary || {});
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch register data', variant: 'destructive' });
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [storeId, selectedTerminalId, isManager, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenRegister = async () => {
    if (!selectedShift) {
      toast({ title: 'Error', description: 'Select a shift', variant: 'destructive' });
      return;
    }
    if (!openingBalance || parseFloat(openingBalance) < 0) {
      toast({ title: 'Error', description: 'Enter valid opening balance', variant: 'destructive' });
      return;
    }
    
    const counterId = selectedTerminalId;
    if (!counterId) {
      toast({ title: 'Error', description: 'No terminal selected', variant: 'destructive' });
      return;
    }
    
    setSubmitting(true);
    try {
      await posAPI.openRegister({
        store_id: storeId,
        counter_id: counterId,
        shift_id: selectedShift,
        opening_balance: parseFloat(openingBalance),
        opening_notes: openingNotes || null
      });
      
      toast({ title: 'Success', description: 'Cash register opened successfully' });
      setOpenDialogOpen(false);
      setOpeningBalance('');
      setOpeningNotes('');
      fetchData();
      terminalContext?.refreshTerminals?.();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to open register', 
        variant: 'destructive' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseRegister = async () => {
    if (!actualBalance || parseFloat(actualBalance) < 0) {
      toast({ title: 'Error', description: 'Enter actual closing balance', variant: 'destructive' });
      return;
    }
    
    // Use selectedRegisterToClose if manager is closing another terminal's register
    const registerToClose = selectedRegisterToClose || currentRegister;
    if (!registerToClose) {
      toast({ title: 'Error', description: 'No register to close', variant: 'destructive' });
      return;
    }
    
    setSubmitting(true);
    try {
      const result = await posAPI.closeRegister(registerToClose.register_id || registerToClose.id, {
        actual_closing_balance: parseFloat(actualBalance),
        closing_notes: closingNotes || null,
        denominations: Object.fromEntries(Object.entries(denominations).filter(([, v]) => parseInt(v, 10) > 0).map(([k, v]) => [k, parseInt(v, 10)]))
      });
      
      const variance = result.data.variance;
      let message = variance === 0 
        ? 'Register closed with no variance' 
        : `Register closed. Variance: ₹${variance.toFixed(2)}`;
      
      if (result.data.is_abnormal) {
        message += ' (Flagged for review)';
      }
      
      toast({ title: 'Success', description: message });
      setCloseDialogOpen(false);
      setActualBalance('');
      setClosingNotes('');
      const closedDenoms = Object.fromEntries(Object.entries(denominations).filter(([, v]) => parseInt(v, 10) > 0).map(([k, v]) => [k, parseInt(v, 10)]));
      setDenominations(emptyDenominations());
      setSelectedRegisterToClose(null);
      
      // Show the closing report automatically
      const closedRegister = {
        ...registerToClose,
        status: 'closed',
        actual_closing_balance: parseFloat(actualBalance),
        closing_notes: closingNotes,
        denominations: Object.keys(closedDenoms).length ? closedDenoms : null,
        closing_time: new Date().toISOString(),
        closed_by_name: user?.full_name || user?.username,
        cash_variance: result.data.variance,
        system_closing_balance: result.data.system_balance,
        total_cash_sales: result.data.total_cash_sales ?? registerToClose?.total_cash_sales,
        cash_change_given: result.data.cash_change_given ?? registerToClose?.cash_change_given,
        total_cash_returns: result.data.total_refunds || 0,
        total_return_refunds: result.data.total_return_refunds || 0,
        total_exchange_refunds: result.data.total_exchange_refunds || 0,
        total_cash_expenses: result.data.total_cash_expenses || 0,
        abnormal_reasons: result.data.abnormal_reasons,
        is_manager_close: !!selectedRegisterToClose
      };
      openReport(closedRegister);
      
      fetchData();
      terminalContext?.refreshTerminals?.();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to close register', 
        variant: 'destructive' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Manager closing another terminal's register
  const openManagerCloseDialog = (terminal) => {
    setSelectedRegisterToClose(terminal.register_info);
    setActualBalance('');
    setClosingNotes('');
    setCloseDialogOpen(true);
  };

  const openOpenDialog = () => {
    setSelectedShift(store?.shifts?.[0]?.shift_id || '');
    setOpeningBalance('');
    setOpeningNotes('');
    setOpenDialogOpen(true);
  };

  const openCloseDialog = () => {
    setActualBalance('');
    setClosingNotes('');
    setCloseDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="cash-register-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Cash Register</h1>
          <p className="text-gray-500">
            {user?.entity_name}
            {currentTerminal && (
              <span className="ml-2 text-emerald-600">
                • Terminal: {currentTerminal.terminal_id}
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} className="border-gray-300">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Tabs for Manager vs My Register */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-100">
          <TabsTrigger value="my-register" className="data-[state=active]:bg-white">
            <Monitor className="w-4 h-4 mr-2" />
            My Register
          </TabsTrigger>
          {isManager && (
            <TabsTrigger value="all-terminals" className="data-[state=active]:bg-white">
              <Users className="w-4 h-4 mr-2" />
              All Terminals
            </TabsTrigger>
          )}
          <TabsTrigger value="history" className="data-[state=active]:bg-white">
            <History className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        {/* My Register Tab */}
        <TabsContent value="my-register" className="space-y-4">
          {/* Current Register Status */}
          {currentRegister ? (
            <Card className="border-emerald-200 bg-emerald-50">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2 text-emerald-800">
                    <CheckCircle2 className="w-5 h-5" />
                    Register Open
                  </CardTitle>
                  <Badge className="bg-emerald-600">Active</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Register Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-3 border border-emerald-200">
                    <p className="text-xs text-gray-500 mb-1">Terminal</p>
                    <p className="font-semibold text-emerald-700">{currentRegister.counter_id}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-emerald-200">
                    <p className="text-xs text-gray-500 mb-1">Shift</p>
                    <p className="font-semibold">{currentRegister.shift_id}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-emerald-200">
                    <p className="text-xs text-gray-500 mb-1">Opened By</p>
                    <p className="font-semibold">{currentRegister.opened_by_name}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-emerald-200">
                    <p className="text-xs text-gray-500 mb-1">Opening Time</p>
                    <p className="font-semibold text-sm">
                      {currentRegister.opening_time ? formatTimeOnly(currentRegister.opening_time) : '-'}
                    </p>
                  </div>
                </div>

                {/* Sales Summary */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-gray-500">Opening Balance</p>
                    <p className="text-lg font-bold text-gray-800">
                      {formatCurrency(currentRegister.opening_balance)}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <p className="text-xs text-gray-500">Cash Sales</p>
                    <p className="text-lg font-bold text-green-600">
                      {formatCurrency(currentRegister.total_cash_sales)}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-blue-200">
                    <p className="text-xs text-gray-500">Card Sales</p>
                    <p className="text-lg font-bold text-blue-600">
                      {formatCurrency(currentRegister.total_card_sales)}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-purple-200">
                    <p className="text-xs text-gray-500">UPI Sales</p>
                    <p className="text-lg font-bold text-purple-600">
                      {formatCurrency(currentRegister.total_upi_sales)}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-emerald-300 bg-emerald-50">
                    <p className="text-xs text-gray-500">Expected Cash</p>
                    <p className="text-lg font-bold text-emerald-700">
                      {formatCurrency(
                        (currentRegister.opening_balance || 0) +
                        (currentRegister.total_cash_sales || 0) -
                        (currentRegister.total_cash_returns || 0)
                      )}
                    </p>
                  </div>
                </div>

                {/* Close Button */}
                <div className="flex justify-end pt-4">
                  <Button 
                    onClick={openCloseDialog}
                    className="bg-red-600 hover:bg-red-700"
                   data-testid="close-register-btn">
                    <Square className="w-4 h-4 mr-2" />
                    Close Register
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-gray-300">
              <CardContent className="py-12 text-center">
                <DollarSign className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Register is Closed</h3>
                <p className="text-gray-500 mb-6">Open a cash register to start billing on this terminal</p>
                
                <Button 
                  onClick={openOpenDialog}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={!selectedTerminalId}
                 data-testid="open-register-btn">
                  <Play className="w-4 h-4 mr-2" />
                  Open Register
                </Button>
                
                {!selectedTerminalId && (
                  <p className="text-sm text-amber-600 mt-4">
                    Please select a terminal from the header dropdown first
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* All Terminals Tab (Manager Only) */}
        {isManager && (
          <TabsContent value="all-terminals" className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600">Total Terminals</p>
                      <p className="text-2xl font-bold text-blue-800">{terminalsSummary.total_terminals || 0}</p>
                    </div>
                    <Monitor className="w-8 h-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-600">Registers Open</p>
                      <p className="text-2xl font-bold text-emerald-800">{terminalsSummary.open_registers || 0}</p>
                    </div>
                    <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Registers Closed</p>
                      <p className="text-2xl font-bold text-gray-800">{terminalsSummary.closed_registers || 0}</p>
                    </div>
                    <Square className="w-8 h-8 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Terminals Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Terminal Register Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Terminal</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Opened By</TableHead>
                      <TableHead>Shift</TableHead>
                      <TableHead>Opening Time</TableHead>
                      <TableHead className="text-right">Opening Bal.</TableHead>
                      <TableHead className="text-right">Cash Sales</TableHead>
                      <TableHead className="text-right">Total Sales</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {terminalsStatus.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                          No terminals configured
                        </TableCell>
                      </TableRow>
                    ) : (
                      terminalsStatus.map((terminal) => (
                        <TableRow key={terminal.terminal_id} className={terminal.is_register_open ? 'bg-emerald-50' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Monitor className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="font-medium">{terminal.terminal_id}</p>
                                <p className="text-xs text-gray-500">{terminal.terminal_name}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {terminal.is_register_open ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                                Open
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-500">
                                Closed
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {terminal.register_info?.opened_by_name ? (
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-gray-400" />
                                <span>{terminal.register_info.opened_by_name}</span>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            {terminal.register_info?.shift_id || '-'}
                          </TableCell>
                          <TableCell>
                            {terminal.register_info?.opening_time 
                              ? formatTimeOnly(terminal.register_info.opening_time)
                              : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {terminal.register_info?.opening_balance != null
                              ? formatCurrency(terminal.register_info.opening_balance)
                              : '-'}
                          </TableCell>
                          <TableCell className="text-right text-green-600 font-medium">
                            {terminal.register_info?.current_cash_sales != null
                              ? formatCurrency(terminal.register_info.current_cash_sales)
                              : '-'}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {terminal.register_info?.total_sales != null
                              ? formatCurrency(terminal.register_info.total_sales)
                              : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {terminal.is_register_open && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => openManagerCloseDialog(terminal)}
                              >
                                <Square className="w-3 h-3 mr-1" />
                                Close
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Register History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Terminal</TableHead>
                    <TableHead>Shift</TableHead>
                    <TableHead>Opened By</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Opening</TableHead>
                    <TableHead className="text-right">Closing</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="text-center">Report</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {registerHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                        No register history
                      </TableCell>
                    </TableRow>
                  ) : (
                    registerHistory.slice(0, 20).map((reg) => (
                      <TableRow key={reg.id}>
                        <TableCell className="font-medium">{reg.register_date}</TableCell>
                        <TableCell>{reg.counter_id}</TableCell>
                        <TableCell>{reg.shift_id}</TableCell>
                        <TableCell>{reg.opened_by_name}</TableCell>
                        <TableCell>
                          {reg.status === 'open' ? (
                            <Badge className="bg-emerald-100 text-emerald-700">Open</Badge>
                          ) : (
                            <Badge variant="outline">Closed</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(reg.opening_balance)}</TableCell>
                        <TableCell className="text-right">
                          {reg.actual_closing_balance != null ? formatCurrency(reg.actual_closing_balance) : '-'}
                          {reg.denominations && (
                            <div className="text-[10px] text-gray-500 font-normal whitespace-nowrap" data-testid={`history-denoms-${reg.id || reg.register_id}`}>
                              {Object.entries(reg.denominations).sort((a, b) => Number(b[0]) - Number(a[0])).map(([d, n]) => `${d}×${n}`).join(' · ')}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${
                          reg.cash_variance > 0 ? 'text-green-600' :
                          reg.cash_variance < 0 ? 'text-red-600' : ''
                        }`}>
                          {reg.cash_variance != null ? formatCurrency(reg.cash_variance) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {reg.status === 'closed' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openReport(reg)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`view-report-${reg.id}`}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Cash Register Closing Report
            </DialogTitle>
          </DialogHeader>
          
          <CashRegisterReport 
            ref={reportRef}
            register={selectedReportRegister}
            storeName={store?.name}
            storeCode={store?.code}
          />
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
              <Printer className="w-4 h-4 mr-2" />
              Print Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Open Register Dialog */}
      <Dialog open={openDialogOpen} onOpenChange={setOpenDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <Play className="w-5 h-5 text-emerald-500" />
              Open Cash Register
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Terminal Info */}
            <div className="space-y-2">
              <Label className="text-gray-700">Terminal</Label>
              <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <Monitor className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="font-medium text-emerald-800">{currentTerminal?.terminal_id}</p>
                  <p className="text-sm text-emerald-600">{currentTerminal?.terminal_name}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700">Shift *</Label>
              <Select value={selectedShift} onValueChange={setSelectedShift}>
                <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="shift-select">
                  <SelectValue placeholder="Select shift..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {store?.shifts?.filter(s => s.is_active).map(shift => (
                    <SelectItem key={shift.shift_id} value={shift.shift_id}>
                      {shift.name} ({shift.start_time} - {shift.end_time})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700">Opening Cash Balance (₹) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(e.target.value)}
                placeholder="Enter opening cash amount"
                className="bg-gray-50 border-gray-300"
                data-testid="opening-balance-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700">Notes (Optional)</Label>
              <Textarea
                value={openingNotes}
                onChange={(e) => setOpeningNotes(e.target.value)}
                placeholder="Any notes..."
                className="bg-gray-50 border-gray-300"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialogOpen(false)} className="border-gray-300">
              Cancel
            </Button>
            <Button 
              onClick={handleOpenRegister} 
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? 'Opening...' : 'Open Register'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Register Dialog */}
      <Dialog open={closeDialogOpen} onOpenChange={(open) => {
        setCloseDialogOpen(open);
        if (!open) setSelectedRegisterToClose(null);
      }}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <Square className="w-5 h-5 text-red-500" />
              Close Cash Register
              {selectedRegisterToClose && isManager && (
                <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">
                  Manager Override
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Show register info - either current or selected for manager */}
            {(selectedRegisterToClose || currentRegister) && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {selectedRegisterToClose && (
                  <div className="flex justify-between pb-2 border-b">
                    <span className="text-gray-600">Opened By:</span>
                    <span className="font-medium text-amber-600">{selectedRegisterToClose.opened_by_name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Opening Balance:</span>
                  <span className="font-medium">
                    {formatCurrency((selectedRegisterToClose || currentRegister)?.opening_balance)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cash Sales:</span>
                  <span className="font-medium text-green-600">
                    + {formatCurrency((selectedRegisterToClose || currentRegister)?.current_cash_sales || (selectedRegisterToClose || currentRegister)?.total_cash_sales || 0)}
                  </span>
                </div>
                {/* Always show Return Refunds and Exchange Refunds separately */}
                {(() => {
                  const reg = selectedRegisterToClose || currentRegister;
                  const totalCashReturns = reg?.total_cash_returns || 0;
                  const trackedReturnRefunds = reg?.total_return_refunds;  // Don't default, check for null
                  const trackedExchangeRefunds = reg?.total_exchange_refunds || 0;
                  
                  // Calculate return refunds correctly:
                  // - If total_return_refunds is explicitly set, use it
                  // - Otherwise, calculate as: total_cash_returns - total_exchange_refunds
                  let displayReturnRefunds = 0;
                  if (trackedReturnRefunds !== null && trackedReturnRefunds !== undefined) {
                    displayReturnRefunds = trackedReturnRefunds;
                  } else if (totalCashReturns > 0) {
                    displayReturnRefunds = Math.max(0, totalCashReturns - trackedExchangeRefunds);
                  }
                  
                  const displayExchangeRefunds = trackedExchangeRefunds;
                  
                  return (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Return Refunds:</span>
                        <span className="font-medium text-orange-600">
                          - {formatCurrency(displayReturnRefunds)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Exchange Refunds:</span>
                        <span className="font-medium text-purple-600">
                          - {formatCurrency(displayExchangeRefunds)}
                        </span>
                      </div>
                    </>
                  );
                })()}
                {((selectedRegisterToClose || currentRegister)?.total_cash_expenses || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cash Expenses:</span>
                    <span className="font-medium text-red-600">
                      - {formatCurrency((selectedRegisterToClose || currentRegister)?.total_cash_expenses || 0)}
                    </span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-semibold">Expected Cash:</span>
                  <span className="font-bold text-emerald-600">
                    {(() => {
                      const reg = selectedRegisterToClose || currentRegister;
                      const opening = reg?.opening_balance || 0;
                      const cashSales = reg?.current_cash_sales || reg?.total_cash_sales || 0;
                      const totalCashReturns = reg?.total_cash_returns || 0;
                      const cashExpenses = reg?.total_cash_expenses || 0;
                      
                      // Expected = Opening + Sales - total_cash_returns - Expenses
                      // Note: total_cash_returns already includes ALL cash refunds (returns + exchanges)
                      // We don't add exchange_refunds separately as it would double-count
                      return formatCurrency(opening + cashSales - totalCashReturns - cashExpenses);
                    })()}
                  </span>
                </div>
              </div>
            )}
            
            <DenominationCounter
              value={denominations}
              onChange={(next) => { setDenominations(next); const t = denominationTotal(next); setActualBalance(t > 0 ? String(t) : ''); }}
            />

            <div className="space-y-2">
              <Label className="text-gray-700">Actual Cash in Drawer (₹) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={actualBalance}
                onChange={(e) => setActualBalance(e.target.value)}
                placeholder="Count and enter actual cash"
                className="bg-gray-50 border-gray-300"
                data-testid="closing-balance-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700">Closing Notes {selectedRegisterToClose ? '(Required for manager close)' : '(Optional)'}</Label>
              <Textarea
                value={closingNotes}
                onChange={(e) => setClosingNotes(e.target.value)}
                placeholder={selectedRegisterToClose ? "Reason for manager override close..." : "Any remarks..."}
                className="bg-gray-50 border-gray-300"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setCloseDialogOpen(false);
              setSelectedRegisterToClose(null);
            }} className="border-gray-300">
              Cancel
            </Button>
            <Button 
              onClick={handleCloseRegister} 
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? 'Closing...' : 'Close Register'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashRegisterPage;
